import { Injectable, OnDestroy } from '@angular/core';
import { BaseLocalStorageService, KeyValue } from '@shared/data-access';
import { FormGroup } from '@angular/forms';
import { Observable, Subject, merge, of, take, takeUntil } from 'rxjs';
import { SimpleTableComponent, TableDataColumn } from '@shared/ui/tables';

@Injectable({
  providedIn: 'root',
})
export class BaseTableFiltersLocalStorageService implements OnDestroy {
  stopPuttingToStorage = new Subject<void>();
  storageFiltersSet = new Subject<void>();

  protected formGroup?: FormGroup;
  protected sortingColumn?: Subject<TableDataColumn> | null;
  protected pageChanged?: Subject<number>;
  protected identifier = '';
  protected isLoadingFromStorage = false;
  protected tableViewChild: SimpleTableComponent | undefined | null;
  protected ngUnsubscribe = new Subject<void>();
  protected readFiltersFromStorage = true;

  private calledOnceWhenFiltersAreCacheFiltersAreTurnedOff = false;

  constructor(protected storage: BaseLocalStorageService) {}

  setUpServiceForComponent(
    identifier: string,
    formGroup: FormGroup,
    sortingColumnSubject: Subject<TableDataColumn> | null,
    tableViewChild: SimpleTableComponent | undefined | null,
    clearFiltersSubject: Subject<void> | null,
    pageChangedSubject?: Subject<number>
  ): void {
    this.identifier = identifier;
    this.formGroup = formGroup;
    this.sortingColumn = sortingColumnSubject;
    this.pageChanged = pageChangedSubject;
    this.tableViewChild = tableViewChild;

    this.autoClearFiltersValueChange()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.putFiltersToLocalStorage();
      });

    this.shouldWorkWithLocalStorage()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((shouldWork) => {
        this.readFiltersFromStorage = shouldWork;
        if (!shouldWork) {
          this.clearFilters();
          this.stopPuttingToStorage.next();
          this.stopPuttingToStorage.complete();

          if (!this.calledOnceWhenFiltersAreCacheFiltersAreTurnedOff) {
            this.calledOnceWhenFiltersAreCacheFiltersAreTurnedOff = true;
            this.storageFiltersSet.next();
          }

          return;
        }

        this.stopPuttingToStorage = new Subject<void>();

        this.formGroup?.valueChanges.pipe(takeUntil(merge(this.ngUnsubscribe, this.stopPuttingToStorage))).subscribe(() => this.putFiltersToLocalStorage());
        this.sortingColumn?.pipe(takeUntil(merge(this.ngUnsubscribe, this.stopPuttingToStorage))).subscribe((x) => this.storage.setItem(`${this.identifier} sort`, x));
        this.sortingColumn?.pipe(take(1)).subscribe((x) => tableViewChild?.setSort(x));
        clearFiltersSubject?.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => this.clearFilters());
        if (this.pageChanged) {
          this.pageChanged.pipe(takeUntil(merge(this.ngUnsubscribe, this.stopPuttingToStorage))).subscribe((page) => this.putPageToLocalStorage(page));
        }

        this.isLoadingFromStorage = true;
        this.getFiltersFromLocalStorage();
      });
  }

  checkIfAdvancedFiltersAreSet(): boolean {
    const filters = this.storage.getItem(`${this.identifier} filters`) as string;
    if (filters !== null) {
      const controls = this.formGroup?.controls;
      if (controls) {
        const values: KeyValue[] = JSON.parse(filters);
        for (const storedFilter of values) {
          if ((storedFilter.value && storedFilter.value.length > 0 && storedFilter.key !== 'search' && storedFilter.key !== 'date') || storedFilter.key === 'range') {
            return true;
          }
        }
      }
    }

    return false;
  }

  resetService(): void {
    this.calledOnceWhenFiltersAreCacheFiltersAreTurnedOff = false;
    this.ngUnsubscribe.next();
  }

  areFiltersLoading(): boolean {
    return this.isLoadingFromStorage;
  }

  autoClearFiltersOn(): boolean {
    return !this.readFiltersFromStorage;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  protected shouldWorkWithLocalStorage(): Observable<boolean> {
    return of(false);
  }

  protected getFiltersFromLocalStorage(isFromParent: boolean = false): void {
    const filters = this.storage.getItem(`${this.identifier} filters`) as string;

    if (filters !== null) {
      const controls = this.formGroup?.controls;
      if (controls) {
        const values: KeyValue[] = JSON.parse(filters);
        for (const storedFilter of values) {
          if (controls[storedFilter.key]) {
            controls[storedFilter.key].setValue(storedFilter.value);
          }
        }
      }
    }

    if ((JSON.parse(this.storage.getItem(`${this.identifier} sort`) as string) as TableDataColumn) !== null) {
      this.sortingColumn?.next(JSON.parse(this.storage.getItem(`${this.identifier} sort`) as string) as TableDataColumn);
    } else {
      this.tableViewChild?.setArrowDirectionOnDefaultSortingColumn();
    }

    if (this.storage.getItem(`${this.identifier} page`) !== null && this.storage.getItem(`${this.identifier} page`) !== 1) {
      const storedPage = this.storage.getItem(`${this.identifier} page`) as string;

      const parsedPage = parseInt(storedPage);

      if (!isNaN(parsedPage)) {
        this.pageChanged?.next(parsedPage);
      }
    }

    if (!isFromParent) {
      this.storageFiltersSet.next();
    }

    this.calledOnceWhenFiltersAreCacheFiltersAreTurnedOff = true;
    this.isLoadingFromStorage = false;
  }

  protected autoClearFiltersValueChange(): Observable<boolean> {
    return of(false);
  }

  private putFiltersToLocalStorage(): void {
    if (!this.isLoadingFromStorage) {
      const filters: KeyValue[] = [];
      const controls = this.formGroup?.controls;

      for (const key in controls) {
        if (controls[key].value !== null && controls[key].value !== '') {
          filters.push(new KeyValue(key, controls[key].value));
        }
      }
      this.storage.setItem(`${this.identifier} filters`, filters);
    }
  }

  private putPageToLocalStorage(page: number): void {
    if (!this.isLoadingFromStorage) {
      this.storage.setItem(`${this.identifier} page`, page);
    }
  }

  private clearFilters(): void {
    this.storage.removeItem(`${this.identifier} filters`);
    this.storage.removeItem(`${this.identifier} sort`);
    this.storage.removeItem(`${this.identifier} page`);
  }
}
